import { useMutation } from '@tanstack/react-query';
import { updateQuoteV2ItemLabelsMutation } from 'Services/Mutations/QuotesV2';
import type { QuoteV2ItemWithTotalsResponse } from 'Services/Query/QuotesV2/Types';
import {
  updateItemInTree,
  updateItemFromServerResponse
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

export interface UpdateLabelsVariables {
  itemId: string;
  labelType: string;
  customLabel: string;
  includeInTotalCost: boolean;
}

/**
 * Update item labels via the dedicated v2 endpoint.
 * Applies optimistic update immediately, then syncs with server response.
 */
export const useUpdateItemLabels = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = updateQuoteV2ItemLabelsMutation();

  return useMutation({
    mutationFn: ({
      itemId,
      labelType,
      customLabel,
      includeInTotalCost
    }: UpdateLabelsVariables) =>
      mutationFn({
        quoteId,
        itemId,
        labelType,
        customLabel,
        includeInTotalCost
      }),

    onMutate: async ({
      itemId,
      labelType,
      customLabel,
      includeInTotalCost
    }) => {
      await cache.cancelItems();
      const previousItems = cache.getItems();

      cache.setItems(old =>
        updateItemInTree(old || [], itemId, {
          labelType,
          customLabel,
          includeInTotalCost
        })
      );

      return { previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: data => {
      const response = data as QuoteV2ItemWithTotalsResponse;

      if (response?.item) {
        cache.setItems(old =>
          updateItemFromServerResponse(old || [], response.item, p => ({
            labelType: p.labelType,
            customLabel: p.customLabel,
            includeInTotalCost: p.includeInTotalCost
          }))
        );
      }

      if (response?.totals) {
        cache.setQuote(old =>
          old
            ? {
                ...old,
                totalSell: response.totals.totalSell,
                totalCost: response.totals.totalCost
              }
            : old
        );
      }

      cache.invalidate();
    }
  });
};
